import React, { useEffect } from 'react';
import { Breadcrumb, Layout, Menu, theme } from 'antd';
import './entry.css';
import AppRoutes from '../AppRoutes';
import UserMenu from '../common/UserMenu';
import Login from '../Login';
import { useApp } from '../../model';
import { createBrowserRouter, RouterProvider, useLocation, useNavigate, Link } from 'react-router-dom';
import { parseQuery, canISeeThis } from '../../utils';
import {
  BODY_PADDING_HORIZON,
  BODY_PADDING_VERTICAL,
  CONTNET_PADDING,
  CONTAINER_HEIGHT,
  ROLES,
} from '../../constants';

const { OBSERVER } = ROLES;

const { Header, Content, Footer } = Layout;

const items: any[] = [
  {
    label: '展会管理',
    key: '/project',
    authControl: {
      rolesCanNotSee: [ROLES.OBSERVER]
    },
  },
  {
    label: '设备查询',
    key: '/devices_global',
    authControl: {
      rolesCanNotSee: [ROLES.OBSERVER]
    },
  },
  {
    label: '工单管理',
    key: '/tasks',
    authControl: {
      rolesCanNotSee: [ROLES.OBSERVER]
    },
  },
  {
    authControl: {
      rolesCanNotSee: [ROLES.ELECTRICIAN, ROLES.MANAGER, OBSERVER], // rolesCanNotSee 优先级更高
    },
    label: '历史数据',
    key: '/history-', // 子菜单的key必须以父菜单的key开头,否则菜单栏无法匹配高亮
    children: [
      {
        label: '实时数据查询',
        key: '/history-logs',
      },
      {
        label: '告警数据查询',
        key: '/history-alarms',
      },
      {
        label: '历史展会查询',
        key: '/history-projects',
      },
    ],
  },
  {
    label: '网关管理',
    key: '/gateway',
    authControl: {
      rolesCanNotSee: [ROLES.ELECTRICIAN, ROLES.OBSERVER, ROLES.MANAGER, ROLES.REGIONAL_HEADER], // rolesCanNotSee 优先级更高
    },
  },
  {
    authControl: {
      rolesCanNotSee: [ROLES.ELECTRICIAN, ROLES.OBSERVER, ROLES.MANAGER, ROLES.REGIONAL_HEADER], // rolesCanNotSee 优先级更高
    },
    label: '系统管理',
    key: '/system_', // 子菜单的key必须以父菜单的key开头,否则菜单栏无法匹配高亮
    children: [
      {
        label: '展馆配置',
        key: '/system_org',
        authControl: {
          rolesCanNotSee: [ROLES.ELECTRICIAN, ROLES.MANAGER], // rolesCanNotSee 优先级更高
        },
      },
      {
        label: '用户管理',
        key: '/system_user',
      },
      {
        label: '4G设备管理',
        key: '/system_devices',
      },
      {
        label: '系统日志',
        key: '/system_logs',
        authControl: {
          rolesCanSee: [ROLES.ADMIN], // rolesCanNotSee 优先级更高
        },
      },
    ]

  },
  {
    label: '任务管理',
    key: '/jobs',
    authControl: {
      rolesCanSee: [ROLES.ADMIN], // rolesCanNotSee 优先级更高
    }
  },
  // {
  //   label: '内容管理',
  //   key: '/cms',
  //   authControl: {
  //     rolesCanSee: [ROLES.ADMIN], // rolesCanNotSee 优先级更高
  //   }
  // },
  {
    label: '监控大屏',
    key: '/dashboard',
    authControl: {
      rolesCanNotSee: [ROLES.ELECTRICIAN], // rolesCanNotSee 优先级更高
    }
  },

]

const RootLayout: React.FC = () => {
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentProjectDetail, currentUser } = useApp();
  const roles = currentUser?.roles || [];
  const menus = items.map(item => {
    const { authControl } = item;
    return canISeeThis(roles, authControl) ? {
      ...item,
      children: (item.children ? item.children.map((c: any) => {
        return canISeeThis(roles, c.authControl) ? {
          ...c,
        } : null
      }).filter(Boolean) : undefined)
    } : null
  }).filter(Boolean) as any[];
  useEffect(() => {
    const cb = (e: any) => {
      console.error(e)
      const message = e?.message;
      if (typeof message === 'string' && message.startsWith('Uncaught TypeError')) {
        navigate('/error')
      }
    };
    window.addEventListener('error', cb);
    return () => {
      window.removeEventListener('error', cb);
    }
  }, [navigate]);
  return (
    <Layout>
      <Header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div className='demo-logo'>
          智慧用电物联平台
        </div>
        {
          currentUser ?
            <Menu
              theme="dark"
              mode="horizontal"
              selectedKeys={[items.find(i => {
                // 子菜单的key必须以父菜单的key开头,否则菜单栏无法匹配高亮
                return location.pathname.includes(i.key)
              })?.key as string]}
              items={menus}
              style={{ flex: 1, minWidth: 0 }}
              onClick={(e) => {
                navigate(e.key)
              }}
            />
            : null
        }
        {/* <LocaltionSelect value={'2'} /> */}
        &nbsp; &nbsp;
        <UserMenu />
      </Header>
      <Content style={{ padding: `${BODY_PADDING_VERTICAL}px ${BODY_PADDING_HORIZON}px` }}>
        <Breadcrumb style={{ margin: '16px 0' }}>
          <Breadcrumb.Item>
            <Link to={'/'}>首页</Link>
          </Breadcrumb.Item>
          {
            window.location.pathname.split('/')
              .map(slug => {
                let target = (items as { label: string, key: string, children: any[] }[]).find((item) => {
                  return item.children?.find(n => n.key === '/' + slug) || (item.key === '/' + slug)
                })
                return (
                  <Breadcrumb.Item key={slug}>{
                    target?.children?.find(i => i.key === '/' + slug)?.label || target?.label
                  }</Breadcrumb.Item>
                )
              })
          }
          {
            location.pathname.includes('/project/') || location.pathname.includes('/history-projects/') ?
              <Breadcrumb.Item>{currentProjectDetail?.name}</Breadcrumb.Item>
              :
              location.pathname.includes('/gateway/') ?
                <Breadcrumb.Item>{parseQuery(location.search).gatewayName}</Breadcrumb.Item>
                : location.pathname.includes('/me') ?
                  <Breadcrumb.Item>用户信息</Breadcrumb.Item>
                  : null
          }
        </Breadcrumb>
        <div
          style={{
            padding: CONTNET_PADDING,
            minHeight: CONTAINER_HEIGHT,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <AppRoutes />
        </div>
      </Content>
      <Footer style={{ justifyContent: 'center', display: 'flex', alignItems: 'center', width: '100%' }}>
        <p style={{fontSize: 12, color: '#666'}}>
          CopyRight©{new Date().getFullYear()} 版权所有 <span style={{ margin: '1em', display: 'inline-block' }}>上海卿誉信息科技有限公司</span>
        </p>
        <img style={{ width: '1em', marginRight: '1em' }} src="/icp.png" alt="备案" />
        <a target='__blank' href="https://beian.miit.gov.cn/#/Integrated/index">沪ICP备19047879号-1</a>
      </Footer>
    </Layout >
  );
};

const rootLayoutRouter = createBrowserRouter([
  {
    path: '/login',
    element: <Login />
  },
  {
    path: '/*',
    element: <RootLayout />
  },
])

export default function Rootlayout() {
  return <RouterProvider router={rootLayoutRouter} />
};